import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Segmented, theme } from 'antd';
import { EmptyState, Spinner } from '@shared/ui';
import { useConversationsQuery } from '@entities/chat';
import { ConversationRow } from './conversation-row';

type Filter = 'all' | 'unread';

export function ConversationsList() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { data, isLoading, isError, error } = useConversationsQuery();
  const [filter, setFilter] = useState<Filter>('all');

  const items = useMemo(() => data?.items ?? [], [data]);
  const unreadCount = useMemo(() => items.filter((c) => c.unreadCount > 0).length, [items]);
  const visible = filter === 'unread' ? items.filter((c) => c.unreadCount > 0) : items;

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return <p className="p-4 text-sm" style={{ color: token.colorError }}>{error?.message ?? 'Error'}</p>;
  }
  if (items.length === 0) {
    return (
      <EmptyState
        className="m-3"
        title={t('chats.empty.title')}
        description={t('chats.empty.description')}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '10px 12px', borderBottom: `1px solid ${token.colorSplit}` }}>
        <Segmented
          size="small"
          block
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          options={[
            { label: `${t('chats.filter.all')} · ${items.length}`, value: 'all' },
            {
              label: `${t('chats.filter.unread')}${unreadCount ? ` · ${unreadCount}` : ''}`,
              value: 'unread',
            },
          ]}
        />
      </div>

      {visible.length === 0 ? (
        <EmptyState className="m-3" title={t('chats.filter.noUnread')} />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, overflowY: 'auto' }}>
          {visible.map((c) => (
            <li key={c.id}>
              <ConversationRow conversation={c} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
